// DESCRIPTION: Multi-step wizard shell for creating/editing events. Manages shared form state,
// step navigation, validation per step, stepper bar, and save/publish actions.
import { __, sprintf } from '@wordpress/i18n';
import { useState, useEffect, useRef, Fragment } from '@wordpress/element';
import { Notice, Spinner } from '@wordpress/components';
import apiFetch from '@wordpress/api-fetch';

import BasicInfoStep from './BasicInfoStep';
import ScheduleLocationStep from './ScheduleLocationStep';
import BookingPricingStep from './BookingPricingStep';
import StylingStep from './StylingStep';
import ReviewStep from './ReviewStep';

const STEPS = [
	{
		key: 'basic',
		label: __( 'Basic Info', 'nemtly-booking' ),
		number: 1,
		heading: __( 'Basic info', 'nemtly-booking' ),
		subtitle: __(
			'Give your event a name and tell visitors what it’s about.',
			'nemtly-booking'
		),
	},
	{
		key: 'schedule',
		label: __( 'Schedule', 'nemtly-booking' ),
		number: 2,
		heading: __( 'Schedule & location', 'nemtly-booking' ),
		subtitle: __(
			'Choose how the event runs, when it’s available, and where it happens.',
			'nemtly-booking'
		),
	},
	{
		key: 'booking',
		label: __( 'Booking', 'nemtly-booking' ),
		number: 3,
		heading: __( 'Booking', 'nemtly-booking' ),
		subtitle: __(
			'Set pricing, capacity, and the timing rules around each booking.',
			'nemtly-booking'
		),
	},
	{
		key: 'styling',
		label: __( 'Styling', 'nemtly-booking' ),
		number: 4,
		heading: __( 'Styling', 'nemtly-booking' ),
		subtitle: __(
			'Customize how the booking page looks. Leave anything blank to inherit your theme’s defaults.',
			'nemtly-booking'
		),
	},
	{
		key: 'review',
		label: __( 'Review', 'nemtly-booking' ),
		number: 5,
		heading: __( 'Review', 'nemtly-booking' ),
		subtitle: __(
			'Everything look right? This is exactly what will be published.',
			'nemtly-booking'
		),
	},
];

const DEFAULT_AVAILABILITY = {
	mon: [ { start: '09:00', end: '17:00' } ],
	tue: [ { start: '09:00', end: '17:00' } ],
	wed: [ { start: '09:00', end: '17:00' } ],
	thu: [ { start: '09:00', end: '17:00' } ],
	fri: [ { start: '09:00', end: '17:00' } ],
	sat: [ { start: '09:00', end: '17:00' } ],
	sun: [ { start: '09:00', end: '17:00' } ],
};

const DEFAULT_FORM_DATA = {
	title: '',
	content: '',
	status: 'draft',
	event_date: '',
	event_end_date: '',
	event_time: '',
	event_end_time: '',
	timezone:
		window.nemtlyCreateEvent?.siteTimezone ||
		Intl.DateTimeFormat().resolvedOptions().timeZone ||
		'',
	event_duration: 60,
	location: '',
	virtual_url: '',
	max_attendees: 0,
	booking_cutoff: 0,
	allow_waitlist: false,
	availability: {},
	event_type: '',
	buffer_before: 0,
	buffer_after: 0,
	featured_image: '',
	price: 0.0,
	categories: [],
	tags: [],
	styling: {
		header_background: '',
		header_title_text: '',
		tag_text: '',
		tag_background: '',
		detail_text: '',
		price_text: '',
		button_background: '',
		button_text: '',
		card_background: '',
		border_color: '',
		card_radius: '',
	},
};

/**
 * Build the initial styling for a brand-new event: the "Mono" template (the
 * documented default look) resolved to its concrete tokens. Falls back to the
 * first available template, or empty styling when none are localized.
 *
 * @since PBOOKING_SINCE
 *
 * @return {Object} Initial styling object.
 */
const getDefaultStyling = () => {
	const templates = window.nemtlyCreateEvent?.templates || {};
	const defaultId = templates.mono ? 'mono' : Object.keys( templates )[ 0 ];
	if ( ! defaultId || ! templates[ defaultId ] ) {
		return { ...DEFAULT_FORM_DATA.styling };
	}
	return {
		template: defaultId,
		...( templates[ defaultId ].tokens || {} ),
	};
};

/**
 * Validates form data for a specific step.
 *
 * Returns an object keyed by field name with error messages.
 *
 * @since PBOOKING_SINCE
 *
 * @param {number} step     Step number (1-based).
 * @param {Object} formData Current form data.
 *
 * @return {Object} Object of { fieldName: errorMessage } (empty if valid).
 */
const validateStep = ( step, formData ) => {
	const errors = {};

	if ( step === 1 ) {
		if ( ! formData.title.trim() ) {
			errors.title = __( 'Event title is required.', 'nemtly-booking' );
		}
	}

	if ( step === 2 ) {
		if ( ! formData.event_type ) {
			errors.event_type = __(
				'Event type is required.',
				'nemtly-booking'
			);
		}

		const isSlotBased =
			formData.event_type === 'one-on-one' ||
			formData.event_type === 'group';

		if ( isSlotBased ) {
			// Slot-based: need availability with at least one active day
			const availability = formData.availability || {};
			const hasActiveDay = Object.values( availability ).some(
				( blocks ) => Array.isArray( blocks ) && blocks.length > 0
			);
			if ( ! hasActiveDay ) {
				errors.availability = __(
					'At least one day with availability is required.',
					'nemtly-booking'
				);
			}
			if ( formData.event_duration <= 0 ) {
				errors.event_duration = __(
					'Slot duration must be a positive number.',
					'nemtly-booking'
				);
			}
		} else if ( formData.event_type === 'event' ) {
			// Date-based: need date and time
			if ( ! formData.event_date ) {
				errors.event_date = __(
					'Event date is required.',
					'nemtly-booking'
				);
			}
			if ( ! formData.event_time ) {
				errors.event_time = __(
					'Start time is required.',
					'nemtly-booking'
				);
			}
		}
	}

	if ( step === 3 ) {
		if ( formData.price < 0 ) {
			errors.price = __( 'Price cannot be negative.', 'nemtly-booking' );
		}
		if ( formData.max_attendees < 0 ) {
			errors.max_attendees = __(
				'Maximum attendees cannot be negative.',
				'nemtly-booking'
			);
		}
	}

	return errors;
};

const EventFormWizard = ( {
	eventId = null,
	mode = 'create',
	onSave = null,
	onCancel = null,
} ) => {
	const [ currentStep, setCurrentStep ] = useState( 1 );
	const [ formData, setFormData ] = useState( () => {
		const initial = { ...DEFAULT_FORM_DATA };
		// New events open on the default ("Mono") template; edit mode keeps
		// whatever styling the event was saved with.
		if ( mode === 'create' && ! eventId ) {
			initial.styling = getDefaultStyling();
		}
		return initial;
	} );
	const [ eventTypes, setEventTypes ] = useState( {} );
	const [ availableCategories, setAvailableCategories ] = useState( [] );
	const [ availableTags, setAvailableTags ] = useState( [] );
	const [ isLoading, setIsLoading ] = useState( false );
	const [ isSaving, setIsSaving ] = useState( false );
	const [ fieldErrors, setFieldErrors ] = useState( {} );
	const [ notices, setNotices ] = useState( [] );

	// localStorage draft autosave (create mode only) + beforeunload tracking.
	const STORAGE_KEY = 'nemtly_event_draft_' + ( eventId || 'new' );
	const isCreateMode = mode === 'create' && ! eventId;
	const savedRef = useRef( false );

	const heading =
		mode === 'edit' && formData.title
			? formData.title
			: mode === 'edit'
			? __( 'Editing Your Event!', 'nemtly-booking' )
			: __( 'Creating Your Event!', 'nemtly-booking' );

	// Load event types
	useEffect( () => {
		apiFetch( { path: '/nemtly-booking/v1/settings/event-types' } )
			.then( ( response ) => {
				if ( response.success ) {
					setEventTypes( response.data );
				}
			} )
			.catch( () => {
				setNotices( ( prev ) => [
					...prev,
					{
						type: 'error',
						message: __(
							'Failed to load event types.',
							'nemtly-booking'
						),
					},
				] );
			} );
	}, [] );

	// Load categories and tags
	useEffect( () => {
		apiFetch( {
			path: '/wp/v2/nemtly_event_category?per_page=100&orderby=name&order=asc',
		} )
			.then( ( data ) =>
				setAvailableCategories( Array.isArray( data ) ? data : [] )
			)
			.catch( () => {} );

		apiFetch( {
			path: '/wp/v2/nemtly_event_tag?per_page=100&orderby=name&order=asc',
		} )
			.then( ( data ) =>
				setAvailableTags( Array.isArray( data ) ? data : [] )
			)
			.catch( () => {} );
	}, [] );

	// Load existing event data for edit mode
	useEffect( () => {
		if ( eventId && mode === 'edit' ) {
			setIsLoading( true );
			apiFetch( { path: `/nemtly-booking/v1/events/${ eventId }` } )
				.then( ( response ) => {
					const event = response?.result ?? response;
					setFormData( {
						title: event.title?.rendered || event.title || '',
						content:
							event.content?.raw || event.content?.rendered || '',
						status: event.status || 'draft',
						event_date: event.event_date || '',
						event_end_date: event.event_end_date || '',
						event_time: event.event_time || '',
						event_end_time: event.event_end_time || '',
						timezone: event.timezone || DEFAULT_FORM_DATA.timezone,
						event_duration: event.event_duration || 60,
						featured_image: event.featured_image || '',
						location: event.location || '',
						virtual_url: event.virtual_url || '',
						max_attendees: event.max_attendees || 0,
						booking_cutoff: event.booking_cutoff || 0,
						allow_waitlist: event.allow_waitlist || false,
						availability: event.availability || {},
						event_type: event.event_type || '',
						buffer_before: parseInt( event.buffer_before ) || 0,
						buffer_after: parseInt( event.buffer_after ) || 0,
						price: parseFloat( event.price ) || 0.0,
						categories: Array.isArray( event.categories )
							? event.categories.map( ( c ) =>
									typeof c === 'object' ? c.id : c
							  )
							: [],
						tags: Array.isArray( event.tags )
							? event.tags.map( ( t ) =>
									typeof t === 'object' ? t.id : t
							  )
							: [],
						styling: {
							...DEFAULT_FORM_DATA.styling,
							...( event.styling || {} ),
						},
					} );
				} )
				.catch( () => {
					setNotices( [
						{
							type: 'error',
							message: __(
								'Failed to load event data.',
								'nemtly-booking'
							),
						},
					] );
				} )
				.finally( () => setIsLoading( false ) );
		}
	}, [ eventId, mode ] );

	// Restore an autosaved create-mode draft on first mount.
	useEffect( () => {
		if ( ! isCreateMode ) {
			return;
		}
		try {
			const raw = localStorage.getItem( STORAGE_KEY );
			if ( ! raw ) {
				return;
			}
			const saved = JSON.parse( raw );
			if ( saved && typeof saved === 'object' && saved.title ) {
				setFormData( ( prev ) => ( { ...prev, ...saved } ) );
				setNotices( ( prev ) => [
					...prev,
					{
						type: 'info',
						message: __(
							'We restored your unsaved draft. It will clear once you publish.',
							'nemtly-booking'
						),
					},
				] );
			}
		} catch ( e ) {
			// Corrupt or unavailable storage — ignore.
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [] );

	// Debounced autosave of form data (create mode only).
	useEffect( () => {
		if ( ! isCreateMode ) {
			return;
		}
		const timer = setTimeout( () => {
			try {
				localStorage.setItem( STORAGE_KEY, JSON.stringify( formData ) );
			} catch ( e ) {
				// Storage full or unavailable — silently skip.
			}
		}, 600 );
		return () => clearTimeout( timer );
	}, [ formData, isCreateMode, STORAGE_KEY ] );

	// Warn before navigating away with unsaved changes.
	useEffect( () => {
		const hasData =
			( formData.title && formData.title.trim() ) ||
			( formData.content && formData.content.trim() );
		if ( ! hasData || savedRef.current ) {
			return;
		}
		const handler = ( e ) => {
			e.preventDefault();
			e.returnValue = '';
		};
		window.addEventListener( 'beforeunload', handler );
		return () => window.removeEventListener( 'beforeunload', handler );
	}, [ formData ] );

	const handleFieldChange = ( field, value ) => {
		setFormData( ( prev ) => ( { ...prev, [ field ]: value } ) );

		// Clear the error for this field when the user edits it
		if ( fieldErrors[ field ] ) {
			setFieldErrors( ( prev ) => {
				const updated = { ...prev };
				delete updated[ field ];
				return updated;
			} );
		}

		// Auto-apply event type defaults in create mode
		if ( field === 'event_type' && value && mode === 'create' ) {
			const selectedType = eventTypes[ value ];
			if ( selectedType ) {
				const isSlotBased = value === 'one-on-one' || value === 'group';

				setFormData( ( prev ) => ( {
					...prev,
					event_type: value,
					event_duration:
						selectedType.duration || prev.event_duration,
					max_attendees:
						selectedType.max_attendees || prev.max_attendees,
					buffer_before: selectedType.buffer_before || 0,
					buffer_after: selectedType.buffer_after || 0,
					booking_cutoff: selectedType.booking_cutoff || 2,
					allow_waitlist: selectedType.allow_waitlist || false,
					// Slot-based: set default Mon-Fri 9-5 availability
					// Event: clear availability, keep date fields
					availability: isSlotBased
						? Object.keys( prev.availability || {} ).length > 0
							? prev.availability
							: { ...DEFAULT_AVAILABILITY }
						: {},
				} ) );

				setNotices( [
					{
						type: 'info',
						message: sprintf(
							/* translators: %s: event type label */
							__(
								'We pre-filled sensible defaults for "%s". Tweak anything on the next steps.',
								'nemtly-booking'
							),
							selectedType.label
						),
					},
				] );
			}
		}
	};

	// Create a new taxonomy term inline, add it to the available list, and
	// select it. Shared by category and tag creation in BasicInfoStep.
	const createTerm = async ( restBase, name, setList, field ) => {
		const trimmed = ( name || '' ).trim();
		if ( ! trimmed ) {
			return;
		}
		try {
			const term = await apiFetch( {
				path: `/wp/v2/${ restBase }`,
				method: 'POST',
				data: { name: trimmed },
			} );
			setList( ( prev ) =>
				prev.some( ( t ) => t.id === term.id )
					? prev
					: [ ...prev, term ]
			);
			setFormData( ( prev ) => ( {
				...prev,
				[ field ]: prev[ field ].includes( term.id )
					? prev[ field ]
					: [ ...prev[ field ], term.id ],
			} ) );
		} catch ( error ) {
			setNotices( ( prev ) => [
				...prev,
				{
					type: 'error',
					message:
						error.message ||
						__(
							'Could not create that term. It may already exist.',
							'nemtly-booking'
						),
				},
			] );
		}
	};

	const handleCreateCategory = ( name ) =>
		createTerm(
			'nemtly_event_category',
			name,
			setAvailableCategories,
			'categories'
		);

	const handleCreateTag = ( name ) =>
		createTerm( 'nemtly_event_tag', name, setAvailableTags, 'tags' );

	const goToStep = ( step ) => {
		// Validate current step before moving forward
		if ( step > currentStep ) {
			const errors = validateStep( currentStep, formData );
			if ( Object.keys( errors ).length > 0 ) {
				setFieldErrors( errors );
				return;
			}
		}

		setFieldErrors( {} );
		setNotices( [] );
		setCurrentStep( step );
	};

	const handleSubmit = async ( isDraft = false ) => {
		// Publish: validate every step and jump to the first error.
		// Draft: allow incomplete data — just clear any stale field errors so
		// the organizer can save a work-in-progress and come back to it.
		if ( ! isDraft ) {
			const allErrors = {};
			let firstErrorStep = null;

			for ( let s = 1; s <= STEPS.length; s++ ) {
				const stepErrors = validateStep( s, formData );
				if ( Object.keys( stepErrors ).length > 0 ) {
					Object.assign( allErrors, stepErrors );
					if ( firstErrorStep === null ) {
						firstErrorStep = s;
					}
				}
			}

			if ( Object.keys( allErrors ).length > 0 ) {
				setFieldErrors( allErrors );
				// Jump to the first step that has errors
				if ( firstErrorStep !== null ) {
					setCurrentStep( firstErrorStep );
				}
				return;
			}
		} else {
			setFieldErrors( {} );
		}

		setIsSaving( true );
		setFieldErrors( {} );
		setNotices( [] );

		const isSlotBased =
			formData.event_type === 'one-on-one' ||
			formData.event_type === 'group';
		const availability = formData.availability || {};

		// For slot-based types, derive event_time from the earliest availability block
		let eventTime = formData.event_time;
		let eventEndTime = formData.event_end_time || '';
		let eventDate = formData.event_date;
		let eventEndDate = formData.event_end_date || formData.event_date;

		if ( isSlotBased ) {
			// Find the earliest start time and latest end time across all active days
			let earliestStart = '23:59';
			let latestEnd = '00:00';

			Object.values( availability ).forEach( ( blocks ) => {
				if ( Array.isArray( blocks ) ) {
					blocks.forEach( ( block ) => {
						if ( block.start < earliestStart ) {
							earliestStart = block.start;
						}
						if ( block.end > latestEnd ) {
							latestEnd = block.end;
						}
					} );
				}
			} );

			eventTime = earliestStart !== '23:59' ? earliestStart : '09:00';
			eventEndTime = latestEnd !== '00:00' ? latestEnd : '17:00';

			// Slot-based events don't have a specific date — use today as placeholder
			if ( ! eventDate ) {
				eventDate = new Date().toISOString().split( 'T' )[ 0 ];
			}
			eventEndDate = eventDate;
		}

		const submitData = {
			title: formData.title,
			content: formData.content,
			status: isDraft ? 'draft' : 'publish',
			event_date: eventDate,
			event_end_date: eventEndDate,
			event_time: eventTime,
			event_end_time: eventEndTime,
			timezone: formData.timezone,
			event_duration: parseInt( formData.event_duration ) || 60,
			location: formData.location,
			virtual_url: formData.virtual_url || '',
			max_attendees: parseInt( formData.max_attendees ) || 0,
			booking_cutoff: parseInt( formData.booking_cutoff ) || 0,
			allow_waitlist: Boolean( formData.allow_waitlist ),
			recurrence_pattern:
				formData.event_type === 'one-on-one' ||
				formData.event_type === 'group'
					? 'availability'
					: 'none',
			recurrence_count:
				formData.event_type === 'one-on-one' ||
				formData.event_type === 'group'
					? 0
					: 1,
			is_recurring:
				formData.event_type === 'one-on-one' ||
				formData.event_type === 'group',
			availability,
			event_type: formData.event_type,
			buffer_before: parseInt( formData.buffer_before ) || 0,
			buffer_after: parseInt( formData.buffer_after ) || 0,
			featured_image: formData.featured_image,
			price: parseFloat( formData.price ) || 0.0,
			categories: formData.categories,
			tags: formData.tags,
			styling: formData.styling || {},
		};

		try {
			const response = await apiFetch( {
				path:
					eventId && mode === 'edit'
						? `/nemtly-booking/v1/events/${ eventId }`
						: '/nemtly-booking/v1/events',
				method: eventId && mode === 'edit' ? 'PUT' : 'POST',
				data: submitData,
			} );

			savedRef.current = true;
			try {
				localStorage.removeItem( STORAGE_KEY );
			} catch ( e ) {
				// Storage unavailable — nothing to clear.
			}

			if ( onSave ) {
				onSave( response );
			}
		} catch ( error ) {
			setNotices( [
				{
					type: 'error',
					message:
						error.message ||
						__( 'Failed to save event.', 'nemtly-booking' ),
				},
			] );
		} finally {
			setIsSaving( false );
		}
	};

	const renderStepContent = () => {
		switch ( currentStep ) {
			case 1:
				return (
					<BasicInfoStep
						formData={ formData }
						onFieldChange={ handleFieldChange }
						fieldErrors={ fieldErrors }
						availableCategories={ availableCategories }
						availableTags={ availableTags }
						onCreateCategory={ handleCreateCategory }
						onCreateTag={ handleCreateTag }
					/>
				);
			case 2:
				return (
					<ScheduleLocationStep
						formData={ formData }
						onFieldChange={ handleFieldChange }
						eventTypes={ eventTypes }
						fieldErrors={ fieldErrors }
					/>
				);
			case 3:
				return (
					<BookingPricingStep
						formData={ formData }
						onFieldChange={ handleFieldChange }
						fieldErrors={ fieldErrors }
					/>
				);
			case 4:
				return (
					<StylingStep
						formData={ formData }
						onFieldChange={ handleFieldChange }
						fieldErrors={ fieldErrors }
					/>
				);
			case 5:
				return (
					<ReviewStep
						formData={ formData }
						eventTypes={ eventTypes }
						availableCategories={ availableCategories }
						availableTags={ availableTags }
					/>
				);
			default:
				return null;
		}
	};

	if ( isLoading ) {
		return (
			<div className="nb-flex nb-items-center nb-justify-center nb-p-16">
				<Spinner />
				<p className="nb-ml-3 nb-text-neutral-700">
					{ __( 'Loading event data…', 'nemtly-booking' ) }
				</p>
			</div>
		);
	}

	const currentStepMeta = STEPS.find( ( s ) => s.number === currentStep );

	return (
		<div className="nemtly-event-wizard nb-bg-neutral-50 nb-min-h-screen">
			{ /* Sticky header */ }
			<div className="nemtly-wizard-header nb-sticky nb-top-[32px] nb-z-50 nb-flex nb-items-center nb-gap-6 nb-bg-neutral-0 nb-border-b nb-border-neutral-200 nb-px-6 nb-py-3 nb-shadow-[0_1px_2px_rgba(0,0,0,0.04)]">
				{ /* Left: back + title */ }
				<div className="nb-flex nb-items-center nb-gap-3 nb-flex-none">
					{ onCancel && (
						<button
							type="button"
							className="nb-flex nb-items-center nb-justify-center nb-w-10 nb-h-10 nb-rounded-lg nb-border nb-border-neutral-300 nb-bg-neutral-0 nb-text-neutral-900 nb-cursor-pointer nb-transition-colors hover:nb-bg-neutral-50"
							onClick={ onCancel }
							title={ __( 'Back to events', 'nemtly-booking' ) }
							aria-label={ __(
								'Back to events',
								'nemtly-booking'
							) }
						>
							<svg
								className="nb-w-5 nb-h-5"
								fill="none"
								stroke="currentColor"
								viewBox="0 0 24 24"
							>
								<path
									strokeLinecap="round"
									strokeLinejoin="round"
									strokeWidth="2"
									d="M15 19l-7-7 7-7"
								/>
							</svg>
						</button>
					) }
					<h2 className="nb-text-lg nb-font-semibold nb-text-neutral-900 nb-m-0 nb-whitespace-nowrap">
						{ heading }
					</h2>
				</div>

				{ /* Center: stepper */ }
				<div className="nemtly-stepper nb-flex nb-items-center nb-flex-1 nb-max-w-[660px] nb-mx-auto">
					{ STEPS.map( ( step, index ) => {
						const isDone = currentStep > step.number;
						const isActive = currentStep === step.number;
						return (
							<Fragment key={ step.key }>
								{ /* Connector line (before every step but the first) */ }
								{ index > 0 && (
									<div className="nb-flex-1 nb-h-px nb-mx-2 nb-min-w-[1rem] nb-bg-neutral-200" />
								) }
								<button
									type="button"
									className="nemtly-stepper-step nb-flex nb-items-center nb-gap-2 nb-flex-none nb-p-1 nb-cursor-pointer nb-bg-transparent nb-border-0"
									aria-current={
										isActive ? 'step' : undefined
									}
									onClick={ () => goToStep( step.number ) }
								>
									<span
										className={ `nemtly-stepper-number nb-flex nb-items-center nb-justify-center nb-w-7 nb-h-7 nb-box-border nb-rounded-full nb-text-xs nb-font-semibold nb-border nb-transition-all ${
											isDone
												? 'nb-bg-accent-500 nb-text-white nb-border-accent-500'
												: isActive
												? 'nb-bg-accent-500 nb-text-white nb-border-accent-500 nb-shadow-[0_0_0_3px_rgba(37,37,37,0.15)]'
												: 'nb-bg-neutral-0 nb-text-neutral-500 nb-border-neutral-300'
										}` }
									>
										{ isDone ? (
											<svg
												className="nb-w-3.5 nb-h-3.5"
												fill="currentColor"
												viewBox="0 0 20 20"
											>
												<path
													fillRule="evenodd"
													d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
													clipRule="evenodd"
												/>
											</svg>
										) : (
											step.number
										) }
									</span>
									<span
										className={ `nb-text-[0.8125rem] nb-whitespace-nowrap nb-hidden md:nb-inline ${
											isActive
												? 'nb-text-neutral-900 nb-font-semibold'
												: isDone
												? 'nb-text-neutral-900 nb-font-medium'
												: 'nb-text-neutral-500 nb-font-medium'
										}` }
									>
										{ step.label }
									</span>
								</button>
							</Fragment>
						);
					} ) }
				</div>

				{ /* Right: actions */ }
				<div className="nb-flex nb-items-center nb-gap-2 nb-flex-none">
					<button
						type="button"
						className="nemtly-button-secondary"
						onClick={ () => handleSubmit( true ) }
						disabled={ isSaving }
					>
						{ __( 'Save as Draft', 'nemtly-booking' ) }
					</button>
					<button
						type="button"
						className="nemtly-button-primary"
						onClick={ () => handleSubmit( false ) }
						disabled={ isSaving }
					>
						{ isSaving
							? __( 'Saving…', 'nemtly-booking' )
							: __( 'Publish', 'nemtly-booking' ) }
					</button>
				</div>
			</div>

			{ /* Body */ }
			<div className="nemtly-wizard-body nb-max-w-[1200px] nb-mx-auto nb-px-6 nb-py-6">
				{ /* Notices — API/network errors and contextual info, not field validation */ }
				{ notices.length > 0 && (
					<div className="nb-flex nb-flex-col nb-gap-3 nb-mb-6">
						{ notices.map( ( notice, index ) => (
							<Notice
								key={ index }
								status={ notice.type }
								onRemove={ () =>
									setNotices( ( prev ) =>
										prev.filter( ( _, i ) => i !== index )
									)
								}
							>
								{ notice.message }
							</Notice>
						) ) }
					</div>
				) }

				{ /* Step heading */ }
				{ currentStepMeta && (
					<div className="nb-mb-5">
						<h2 className="nb-text-lg nb-font-semibold nb-text-neutral-900 nb-m-0">
							{ currentStepMeta.heading }
						</h2>
						<p className="nb-text-sm nb-text-neutral-700 nb-mt-1 nb-mb-0">
							{ currentStepMeta.subtitle }
						</p>
					</div>
				) }

				{ renderStepContent() }

				{ /* Step navigation footer */ }
				<div className="nemtly-wizard-footer nb-flex nb-justify-between nb-items-center nb-mt-6 nb-pt-6 nb-border-t nb-border-neutral-200">
					<button
						type="button"
						className="nemtly-button-secondary"
						onClick={ () => goToStep( currentStep - 1 ) }
						disabled={ currentStep === 1 }
					>
						{ __( 'Back', 'nemtly-booking' ) }
					</button>
					{ currentStep < STEPS.length ? (
						<button
							type="button"
							className="nemtly-button-primary"
							onClick={ () => goToStep( currentStep + 1 ) }
						>
							{ __( 'Continue', 'nemtly-booking' ) }
						</button>
					) : (
						<button
							type="button"
							className="nemtly-button-primary"
							onClick={ () => handleSubmit( false ) }
							disabled={ isSaving }
						>
							{ isSaving
								? __( 'Publishing…', 'nemtly-booking' )
								: mode === 'edit'
								? __( 'Update Event', 'nemtly-booking' )
								: __( 'Publish Event', 'nemtly-booking' ) }
						</button>
					) }
				</div>
			</div>
		</div>
	);
};

export default EventFormWizard;
